import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "./StakingStakesPage.css";

const DEFAULT_PAGE_LIMIT = 50;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Cancelled Staking";

const extractDecimal = (v) => {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const formatDateExport = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const formatNum = (n) =>
  n != null && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 12 })
    : "—";

const formatDec = (v) => {
  const x = extractDecimal(v);
  return x != null ? formatNum(x) : "—";
};

const formatNumPlain = (n) =>
  n != null && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString("en-IN", {
        maximumFractionDigits: 12,
        useGrouping: false,
      })
    : "";

const formatDecPlain = (v) => {
  const x = extractDecimal(v);
  return x != null ? formatNumPlain(x) : "";
};

const userFullName = (u) => {
  if (!u) return "—";
  const parts = [u.first_name ?? u.firstName, u.last_name ?? u.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const planLabel = (row) => {
  const plan = row?.plan;
  const c = row?.currency ?? plan?.currency ?? plan?.short_name;
  const d = row?.duration_days ?? plan?.duration_days;
  const r = plan?.return_percentage;
  if (c != null && d != null && r != null) return `${c} · ${d}d · ${r}%`;
  if (c != null && d != null) return `${c} · ${d}d`;
  return c || plan?._id || plan?.id || "—";
};

const CancelledStakingPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [planId, setPlanId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState({ type: "", text: "" });
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 800);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedUser, currencyId, planId, from, to, pageLimit]);

  const buildFilterParams = useCallback(
    (pageNum, limitNum) => {
      const params = { page: pageNum, limit: limitNum };
      if (debouncedUser) params.user = debouncedUser;
      if (currencyId.trim()) params.currency_id = currencyId.trim();
      if (planId.trim()) params.plan_id = planId.trim();
      if (from) {
        const d = new Date(from);
        if (!Number.isNaN(d.getTime())) params.from = d.toISOString().slice(0, 10);
      }
      if (to) {
        const d = new Date(to);
        if (!Number.isNaN(d.getTime())) params.to = d.toISOString().slice(0, 10);
      }
      return params;
    },
    [debouncedUser, currencyId, planId, from, to]
  );

  const buildParams = useCallback(
    () => buildFilterParams(page, pageLimit),
    [buildFilterParams, page, pageLimit]
  );

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getCancelledUserEarningPlans(buildParams());
        const list = Array.isArray(res?.data) ? res.data : [];
        setRows(list);
        setTotalPages(res?.pagination?.total_pages || 1);
        setTotalCount(
          typeof res?.pagination?.total === "number" ? res.pagination.total : null
        );
        if (res?.success === false && !list.length) {
          setError(res?.message || "Failed to load cancelled stakes.");
        }
      } catch (e) {
        console.error(e);
        setRows([]);
        setError(
          e?.response?.data?.message || e?.message || "Failed to load cancelled stakes."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
  }, [buildParams]);

  const rowToExport = (row) => {
    const u = row.user || {};
    return {
      "Subscription ID": row.id ?? row._id ?? "",
      UUID: u.uuid ?? "",
      Name: userFullName(u) === "—" ? "" : userFullName(u),
      Email: u.email ?? "",
      Phone: u.phone ?? "",
      Plan: planLabel(row) === "—" ? "" : planLabel(row),
      Currency: row.currency ?? row.plan?.currency ?? "",
      "Duration (days)": row.duration_days ?? row.plan?.duration_days ?? "",
      Invested: formatDecPlain(row.invested_amount),
      "Expected Return": formatDecPlain(row.expected_return),
      "USDT Value": formatDecPlain(row.usdt_value),
      Status: row.status ?? "",
      "Cancel Fee %": formatDecPlain(row.cancel_fee_percent),
      "Cancel Fee Amount": formatDecPlain(row.cancel_fee_amount),
      "Refund Amount": formatDecPlain(row.refund_amount),
      "Cancelled At": formatDateExport(row.cancelled_at),
      "Start Date": formatDateExport(row.start_date),
      "End Date": formatDateExport(row.end_date),
      "Created At": formatDateExport(row.createdAt),
      "Purchased By": row.purchased_by ?? "",
      "Wallet Type": row.wallet_type ?? "",
      Notes: row.notes ?? "",
    };
  };

  const fetchAllFiltered = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];
    do {
      const res = await AdminService.getCancelledUserEarningPlans(
        buildFilterParams(currentPage, EXPORT_BATCH)
      );
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setToast({ type: "", text: "" });
    try {
      const allRows = await fetchAllFiltered();
      if (!allRows.length) {
        setToast({ type: "error", text: "No cancelled stakes match your filters to export." });
        setTimeout(() => setToast({ type: "", text: "" }), 3000);
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(allRows.map(rowToExport));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `cancelled_staking_${dateStamp}.xlsx`);
      setToast({ type: "success", text: `Exported ${allRows.length} cancelled stake(s) to Excel.` });
      setTimeout(() => setToast({ type: "", text: "" }), 4000);
    } catch (e) {
      console.error(e);
      setToast({ type: "error", text: e?.message || "Failed to export Excel file." });
      setTimeout(() => setToast({ type: "", text: "" }), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const pageStart = rows.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = rows.length === 0 ? 0 : (page - 1) * pageLimit + rows.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/staking/user-stakes">Staking</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Cancelled Staking</span>
          </p>
          <h1 className="dashboard-hero__title">Cancelled Staking</h1>
          <p className="dashboard-hero__subtitle">
            Cancelled earning subscriptions with cancel fee and refund details
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-ban" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-ban" />
              </span>
              <h2 className="openorders-section__title">Cancelled subscriptions</h2>
            </div>
            <div className="openorders-section__stats staking-stakes__header-actions">
              <span className="openorders-stat">
                <strong>{rows.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    · <strong>{totalCount}</strong> total
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="staking-stakes__export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered cancelled stakes as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          {error && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          )}
          {toast.text && (
            <p
              style={{
                color: toast.type === "error" ? "#dc2626" : "#166534",
                padding: "0 0.5rem 0.75rem",
                margin: 0,
              }}
              role="status"
            >
              {toast.text}
            </p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="User (email / UUID / phone)"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 200, flex: "1 1 220px" }}
            />
            <input
              type="text"
              placeholder="Currency ObjectId"
              value={currencyId}
              onChange={(e) => setCurrencyId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 200px" }}
            />
            <input
              type="text"
              placeholder="Plan (package) ObjectId"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 200px" }}
            />
            <label className="staking-page-limit">
              <span>From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
              />
            </label>
            <label className="staking-page-limit">
              <span>To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
              />
            </label>
            <label className="staking-page-limit">
              <span>Rows per page</span>
              <select
                value={pageLimit}
                onChange={(e) => setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT)}
                className="openorders-search-input staking-page-limit__select"
                disabled={loading}
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : rows.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Invested</th>
                      <th>Cancel fee</th>
                      <th>Refund</th>
                      <th>Cancelled at</th>
                      <th>Status</th>
                      <th style={{ width: "1%", whiteSpace: "nowrap" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const u = row.user || {};
                      const userId = row.user_id ?? u.id;
                      const rowKey = row.id ?? row._id ?? `${userId}-${row.cancelled_at}`;
                      const currency = row.currency ?? row.plan?.currency ?? "";
                      return (
                        <tr key={rowKey}>
                          <td>
                            {userId ? (
                              <Link
                                to={`/dashboard/trader/${userId}`}
                                style={{ color: "#dc2626", fontWeight: 600, textDecoration: "none" }}
                              >
                                {u.uuid ?? "—"}
                              </Link>
                            ) : (
                              u.uuid ?? "—"
                            )}
                          </td>
                          <td>{userFullName(u)}</td>
                          <td>{u.email ?? "—"}</td>
                          <td>{planLabel(row)}</td>
                          <td>
                            {formatDec(row.invested_amount)}
                            {currency ? ` ${currency}` : ""}
                          </td>
                          <td>
                            {formatDec(row.cancel_fee_amount)}
                            {row.cancel_fee_percent != null
                              ? ` (${formatDec(row.cancel_fee_percent)}%)`
                              : ""}
                          </td>
                          <td>
                            <strong>{formatDec(row.refund_amount)}</strong>
                            {currency ? ` ${currency}` : ""}
                          </td>
                          <td>{formatDate(row.cancelled_at)}</td>
                          <td>
                            <span className="staking-stakes__payout-badge staking-stakes__payout-badge--inactive">
                              {row.status ?? "CANCELLED"}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="staking-stakes__view-btn"
                              onClick={() => setDetailRow(row)}
                            >
                              <i className="fas fa-eye" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div
                className="staking-stakes__pagination"
                style={{
                  padding: "0.75rem 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  style={{ margin: 0 }}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="openorders-stat" style={{ margin: 0 }}>
                  Showing {pageStart}–{pageEnd}
                  {totalCount != null ? ` of ${totalCount}` : ""}
                  {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
                </span>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  style={{ margin: 0 }}
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <p className="openorders-empty">No cancelled stakes match your filters.</p>
          )}
        </section>
      </div>

      {detailRow && (
        <div
          className="staking-payout-overlay"
          onClick={() => setDetailRow(null)}
          role="presentation"
        >
          <div
            className="staking-cancel-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancelled-stake-detail-title"
          >
            <div className="staking-cancel-modal__head">
              <h2 id="cancelled-stake-detail-title">Cancelled stake details</h2>
              <button
                type="button"
                className="staking-payout-modal__close"
                onClick={() => setDetailRow(null)}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="staking-cancel-modal__body">
              <dl className="staking-cancel-success__grid">
                <div className="staking-cancel-success__item">
                  <dt>User</dt>
                  <dd>
                    {userFullName(detailRow.user)} · {detailRow.user?.uuid || "—"}
                  </dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Email</dt>
                  <dd>{detailRow.user?.email || "—"}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Plan</dt>
                  <dd>{planLabel(detailRow)}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Invested</dt>
                  <dd>
                    {formatDec(detailRow.invested_amount)}{" "}
                    {detailRow.currency || detailRow.plan?.currency || ""}
                  </dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Expected return</dt>
                  <dd>{formatDec(detailRow.expected_return)}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Cancel fee</dt>
                  <dd>
                    {formatDec(detailRow.cancel_fee_amount)}
                    {detailRow.cancel_fee_percent != null
                      ? ` (${formatDec(detailRow.cancel_fee_percent)}%)`
                      : ""}
                  </dd>
                </div>
                <div className="staking-cancel-success__item staking-cancel-success__item--refund">
                  <dt>Refund</dt>
                  <dd>{formatDec(detailRow.refund_amount)}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Cancelled at</dt>
                  <dd>{formatDate(detailRow.cancelled_at)}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Start → End</dt>
                  <dd>
                    {formatDate(detailRow.start_date)} → {formatDate(detailRow.end_date)}
                  </dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Purchased by</dt>
                  <dd>{detailRow.purchased_by || "—"}</dd>
                </div>
                <div className="staking-cancel-success__item">
                  <dt>Subscription ID</dt>
                  <dd>
                    <code>{detailRow.id ?? detailRow._id ?? "—"}</code>
                  </dd>
                </div>
                {detailRow.notes ? (
                  <div className="staking-cancel-success__item">
                    <dt>Notes</dt>
                    <dd>{detailRow.notes}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
            <div className="staking-cancel-modal__foot">
              <button
                type="button"
                className="staking-payout-btn-close"
                onClick={() => setDetailRow(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default CancelledStakingPage;
